import { useForm, FormProvider, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { Clock } from "lucide-react";
import { FormField } from "@/components/form/FormField";
import ApproverPicker, { type ApproverPrincipal } from "@/components/shared/pickers/approver-picker";
import {
  CheckpointDecisionFormField,
  isDecisionFormValid,
} from "./checkpoint-decision-form-field";
import { useUpdateStep } from "@/hooks/api/mutations/workflows.mutations";
import { useCurrentOrg } from "@/stores";
import type { WorkflowStep } from "@/types/workflow.types";

const schema = z.object({
  title: z.string().min(1, "Title required"),
  description: z.string().optional(),
  approvers: z
    .array(z.object({ principalKind: z.enum(["user", "role", "group"]), principalId: z.string(), label: z.string() }))
    .min(1, "At least one approver required"),
  approvalQuorum: z.enum(["any", "majority", "all"]),
  expiresInMinutes: z.number().int().positive().optional(),
  decisionForm: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function toFormValues(config: Record<string, unknown>): FormValues {
  const raw = Array.isArray(config.approvers) ? (config.approvers as Array<Record<string, string>>) : [];
  const approvers: ApproverPrincipal[] = raw.map((a) => ({
    principalKind: (["user", "role", "group"].includes(a.kind) ? a.kind : "user") as ApproverPrincipal["principalKind"],
    principalId: String(a.id ?? ""),
    label: String(a.id ?? ""),
  }));
  const quorum = typeof config.approval_quorum === "number" ? config.approval_quorum : 1;
  const approvalQuorum =
    quorum === 1 ? "any" : quorum >= (approvers.length || 1) ? "all" : "majority";
  return {
    title: String(config.title ?? ""),
    description: String(config.description ?? ""),
    approvers,
    approvalQuorum: approvalQuorum as "any" | "majority" | "all",
    expiresInMinutes:
      typeof config.expires_in_seconds === "number"
        ? Math.round(config.expires_in_seconds / 60)
        : undefined,
    decisionForm: config.decision_form
      ? JSON.stringify(config.decision_form, null, 2)
      : "",
  };
}

function quorumToInt(q: "any" | "majority" | "all", count: number) {
  if (q === "any") return 1;
  if (q === "all") return count;
  return Math.ceil(count / 2);
}

function toPayload(values: FormValues): Record<string, unknown> {
  let decisionFormParsed: unknown = null;
  if (values.decisionForm?.trim()) {
    try { decisionFormParsed = JSON.parse(values.decisionForm); } catch { /* blocked by isDecisionFormValid gate */ }
  }
  return {
    title: values.title,
    description: values.description ?? "",
    approvers: values.approvers.map((a) => ({ kind: a.principalKind, id: a.principalId })),
    approval_quorum: quorumToInt(values.approvalQuorum, values.approvers.length),
    expires_in_seconds: values.expiresInMinutes ? values.expiresInMinutes * 60 : null,
    decision_form: decisionFormParsed,
  };
}

interface CheckpointConfigFormProps {
  step: WorkflowStep;
  workflowId: string;
  onSuccess?: () => void;
}

export function CheckpointConfigForm({ step, workflowId, onSuccess }: CheckpointConfigFormProps) {
  const t = useTranslations("workflows");
  const updateStep = useUpdateStep();
  const org = useCurrentOrg();

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(step.config),
  });

  const { handleSubmit, register, watch, control, formState: { errors, isSubmitting } } = methods;
  const expiresInMinutes = watch("expiresInMinutes");
  const decisionForm = watch("decisionForm") ?? "";

  const onSubmit = handleSubmit(async (values) => {
    if (!isDecisionFormValid(values.decisionForm ?? "")) return;
    await updateStep.mutateAsync({ workflowId, stepId: step.id, config: toPayload(values) });
    onSuccess?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
            {t("drawer.field.title")} <span className="text-[var(--destructive)]">*</span>
          </label>
          <input
            {...register("title")}
            placeholder="Approval required"
            className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
          />
          {errors.title && <p className="mt-1 text-xs text-[var(--destructive)]">{errors.title.message}</p>}
        </div>

        <FormField label={t("drawer.field.description")} error={errors.description?.message}>
          <textarea
            {...register("description")}
            rows={2}
            placeholder="Describe what needs to be approved…"
            className="w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm focus:border-[var(--ring)] focus:outline-none resize-y"
          />
        </FormField>

        <FormField label={t("drawer.field.approvers")} required error={(errors.approvers as { message?: string } | undefined)?.message}>
          <Controller
            control={control}
            name="approvers"
            render={({ field }) => (
              <ApproverPicker
                orgId={org?.id ?? ""}
                value={field.value as ApproverPrincipal[]}
                onChange={field.onChange}
              />
            )}
          />
        </FormField>

        <FormField label={t("drawer.field.approval_policy")} error={errors.approvalQuorum?.message}>
          <Controller
            control={control}
            name="approvalQuorum"
            render={({ field }) => (
              <div className="inline-flex w-full rounded-lg border border-[var(--border)] bg-[var(--background)] p-0.5">
                {(["any", "majority", "all"] as const).map((opt) => {
                  const active = field.value === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => field.onChange(opt)}
                      className={`h-8 flex-1 rounded-md px-2.5 text-xs font-medium transition-colors ${
                        active
                          ? "bg-[var(--primary)] text-[var(--primary-foreground)] shadow-sm"
                          : "text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--foreground)]"
                      }`}
                    >
                      {opt === "any" ? "Any one" : opt === "majority" ? "Majority" : "All"}
                    </button>
                  );
                })}
              </div>
            )}
          />
        </FormField>

        <FormField
          label={t("drawer.field.timeout_minutes")}
          hint={expiresInMinutes ? `Expires in ${expiresInMinutes} minute(s)` : "No timeout"}
          error={errors.expiresInMinutes?.message}
        >
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              {...register("expiresInMinutes", { valueAsNumber: true })}
              placeholder="60"
              className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
            />
            <Clock className="size-4 shrink-0 text-[var(--muted-foreground)]" />
          </div>
        </FormField>

        <Controller
          control={control}
          name="decisionForm"
          render={({ field }) => (
            <CheckpointDecisionFormField
              label={t("drawer.field.decision_form")}
              value={field.value ?? ""}
              onChange={field.onChange}
              hint="JSON Schema Draft-07 subset — defines the approval form fields"
            />
          )}
        />

        {updateStep.isError && (
          <p className="text-xs text-[var(--destructive)]">{t("drawer.save_error")}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || updateStep.isPending || !isDecisionFormValid(decisionForm)}
          className="h-9 w-full rounded-lg bg-[var(--primary)] text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90 disabled:opacity-60"
        >
          {isSubmitting || updateStep.isPending ? t("drawer.saving") : t("drawer.save")}
        </button>
      </form>
    </FormProvider>
  );
}

export default CheckpointConfigForm;
